<template>
    <VJoyHighlight class="overall-rating" level="neutral">
        <VJoyWrapper :item-stretch="false" :no-margin="true" align="flex-start" direction="column" justify="flex-start" row-gap="4">
            <VJoyWrapper :no-margin="true" class="overall-rating__header" direction="column" item-stretch justify="center" row-gap="1">
                <VJoyText preset="secondary-600" tag-name="h2">{{ formatter.format(ratings.general) }}</VJoyText>
                <VJoyRatingStars :rating-value="ratings.general" size="large" />
                <VJoyText preset="primary-200" tag-name="p">
                    {{ t('profile.resume.appraisals.rating.out.of', {count: appraisalCounter}) }}
                </VJoyText>
            </VJoyWrapper>
            <VJoyWrapper
                :no-margin="true"
                :style="{display: collapsed ? 'none' : 'flex'}"
                class="overall-rating__details"
                direction="column"
                justify="flex-start"
                row-gap="4"
            >
                <RatingDetailItem
                    :icon="'check-circle' as JoyIconsId"
                    :rating="ratings.quality"
                    :title="t('profile.resume.appraisals.label.quality')"
                />
                <RatingDetailItem :icon="'clock' as JoyIconsId" :rating="ratings.delay" :title="t('profile.resume.appraisals.label.deadline')" />
                <RatingDetailItem
                    :icon="'message' as JoyIconsId"
                    :rating="ratings.communication"
                    :title="t('profile.resume.appraisals.label.communication')"
                />
            </VJoyWrapper>
            <div class="overall-rating__toggle">
                <ShowMore
                    :collapsed="collapsed"
                    :label="collapsed ? t('profile.resume.appraisals.show.details') : t('profile.resume.appraisals.hide.details')"
                    size="small"
                    test-id="rating-details-toggle"
                    @click="toggleCollapsed"
                />
            </div>
        </VJoyWrapper>
    </VJoyHighlight>
</template>

<script lang="ts" setup>
    import {ref} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyHighlight, VJoyRatingStars, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import ShowMore from '~/components/_common/show-more/ShowMore.vue';
    import RatingDetailItem from '~/features/appraisals/ui/RatingDetailItem.vue';
    import type {AppraisalAverageRatingResource} from '~/types/api-raw-types';
    import {useLocale} from '#build/imports';

    const {locale} = useLocale();
    const {t} = useTranslation();

    defineProps<{
        ratings: AppraisalAverageRatingResource;
        appraisalCounter: number;
    }>();

    const collapsed = ref(true);

    function toggleCollapsed() {
        collapsed.value = !collapsed.value;
    }

    const formatter = new Intl.NumberFormat(locale.value, {minimumFractionDigits: 1, maximumFractionDigits: 1});
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .overall-rating {
        background-color: var(--joy-color-neutral-10);
        border: 1px solid var(--joy-color-neutral-30);
        width: 100%;
        max-width: 100%;
        flex-shrink: 0;

        @include joy.screen_xs(min) {
            max-width: 180px;
        }

        &__header {
            border-bottom: 1px solid var(--joy-color-neutral-30);
            padding-bottom: var(--joy-core-spacing-5);
            padding-top: var(--joy-core-spacing-2);
            width: 100%;
        }

        &__details {
            width: 100%;
            align-items: center;

            @include joy.screen_xs(min) {
                display: flex !important;
                align-items: flex-start;
            }
        }

        &__toggle {
            display: block;
            width: 100%;

            :deep(.joy-button) {
                width: 100%;
            }

            @include joy.screen_xs(min) {
                display: none;
            }
        }
    }
</style>
